import { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";
import { useIntl } from "react-intl";
import { EventListener } from "react-lottie";

import { ConfluenceLogo } from "@atlaskit/logo";
import { media } from "@atlaskit/primitives";
import Spinner from "@atlaskit/spinner";

import { wacToken } from "@/libraries/design/foundations";
import { withFeatureExposed } from "@/utils/feature-exposed/index.ts";

import { LottieAnimation } from "./lottie-animation.tsx";

const backdropContainer = css({
    display: "flex",
    position: "fixed",
    justifyContent: "center",
    alignItems: "center",
    insetBlockStart: "0px",
    insetInlineEnd: "0px",
    insetInlineStart: "0px",
    insetBlockEnd: "0px"
});

const modalContainer = css({
    backgroundColor: wacToken("color.neutral.light0"),
    boxShadow:
        // eslint-disable-next-line local/ensure-wac-token-usage
        "0 8px 12px rgba(9, 30, 66, 0.15), 0 0 1px rgba(9, 30, 66, 0.31)",
    borderRadius: "4px",
    overflow: "hidden",
    display: "flex",
    justifyContent: "left",
    alignItems: "center",
    margin: "auto 32px",
    padding: 24,
    minHeight: 665,
    width: 1130,
    maxWidth: "100%",
    [media.below.sm]: {
        margin: "auto 8px"
    }
});

const progressContainer = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    width: "100%"
});

const logoContainer = css({
    "marginBlockEnd": 70,
    // eslint-disable-next-line
    " svg": {
        height: "32px !important"
    },
    [media.below.sm]: {
        marginBlockEnd: 100
    }
});

const animationContainer = css({
    "display": "flex",
    "marginBlockEnd": "-100px",
    // eslint-disable-next-line
    "> div": {
        height: 500
    },
    "maxWidth": 500,
    [media.below.sm]: {
        "marginBlockStart": "-100px",
        // eslint-disable-next-line
        "> div": {
            height: 406
        },
        "maxWidth": 406
    }
});
const animationTextContainer = css({
    display: "flex",
    alignItems: "center",
    marginBlockStart: 25,
    textAlign: "center",
    [media.below.sm]: {
        display: "block",
        marginBlockStart: 30
    }
});
const animationHeader = css({
    marginBlockStart: 0,
    marginBlockEnd: 0,
    marginInlineStart: 12,
    fontWeight: 600,
    fontSize: 29,
    [media.below.sm]: {
        marginBlockStart: 16,
        fontWeight: 500,
        fontSize: 24
    }
});
const animationSubtext = css({
    marginBlockStart: 80,
    marginBlockEnd: 0,
    marginInlineStart: 6,
    marginInlineEnd: 6,
    textAlign: "center",
    fontSize: 16,

    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "#44546F",
    [media.below.md]: {
        fontSize: 14
    }
});

const loopBeginIndex = 1;
const loopEndIndex = 3;
const outroAnimationIndex = 4;

interface LoadingAnimationComponentProps {
    isLoadingComplete: boolean;
}

const LoadingAnimationComponent = ({
    isLoadingComplete
}: LoadingAnimationComponentProps) => {
    const { locale } = useIntl();
    const [currentIndex, setCurrentIndex] = useState(0);
    const [animationsMap, setAnimationsMap] = useState({
        en: []
    });

    const getAnimationMap = (locale: string) => {
        const keys = Object.keys(animationsMap);
        const validLocale = keys.includes(locale);
        if (validLocale) {
            // @ts-ignore
            return animationsMap[locale];
        } else {
            return animationsMap["en"];
        }
    };
    useEffect(() => {
        const runSetAnimationMap = async () => {
            const [
                intro,
                outro,
                EN02,
                EN03,
                EN04,
                DE02,
                DE03,
                DE04,
                FR02,
                FR03,
                FR04,
                ES02,
                ES03,
                ES04,
                JA02,
                JA03,
                JA04,
                PTBR02,
                PTBR03,
                PTBR04
            ] = await Promise.all([
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/Intro.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/Outro.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/EN02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/EN03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/EN04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/DE02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/DE03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/DE04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/FR02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/FR03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/FR04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/ES02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/ES03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/ES04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/JA02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/JA03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/JA04.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/PTBR02.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/PTBR03.json"
                ) as Promise<never>,
                import(
                    "@/components/signup/bxp-ui-signup/provisioning/layouts/animations/PTBR04.json"
                ) as Promise<never>
            ]);
            const animationsMap = {
                en: [intro, EN02, EN03, EN04, outro],
                de: [intro, DE02, DE03, DE04, outro],
                fr: [intro, FR02, FR03, FR04, outro],
                es: [intro, ES02, ES03, ES04, outro],
                ja: [intro, JA02, JA03, JA04, outro],
                br: [intro, PTBR02, PTBR03, PTBR04, outro]
            };
            setAnimationsMap(animationsMap);
        };
        runSetAnimationMap();
    }, [animationsMap]);

    // @ts-ignore
    const animations = getAnimationMap(locale);

    const eventListener: EventListener[] = [
        {
            eventName: "complete",
            callback: () => {
                // When provisioning is complete, play outro animation.
                // When outro animation is complete, redirect to product.
                if (isLoadingComplete) {
                    setCurrentIndex(outroAnimationIndex);
                    return;
                }

                // Increment animation inside loop
                else if (currentIndex < loopEndIndex) {
                    setCurrentIndex(currentIndex + 1);
                }

                // Wrap back to beginning of loop
                else if (currentIndex === loopEndIndex) {
                    setCurrentIndex(loopBeginIndex);
                }
            }
        }
    ];

    return (
        <>
            <div css={backdropContainer}>
                <div css={modalContainer}>
                    <div
                        css={progressContainer}
                        data-testid="signup-provisioning-module"
                    >
                        <div css={logoContainer}>
                            <ConfluenceLogo appearance="brand" size="large" />
                        </div>
                        <div css={animationContainer}>
                            {animationsMap && (
                                <LottieAnimation
                                    options={{
                                        loop: false,
                                        autoplay: true,
                                        animationData: animations[currentIndex],
                                        rendererSettings: {
                                            preserveAspectRatio:
                                                "xMidYMid slice"
                                        }
                                    }}
                                    eventListener={eventListener}
                                    isAnimationBrandRefresh={true}
                                />
                            )}
                        </div>
                        <div css={animationTextContainer}>
                            <Spinner size={"medium"} />
                            {currentIndex <= 1 && (
                                <h1 css={animationHeader}>
                                    <FormattedMessage
                                        id="SignupProvisioning.inProgressText.confluence.preparingWorkspace"
                                        defaultMessage="Preparing your workspace..."
                                    />
                                </h1>
                            )}
                            {currentIndex === 2 && (
                                <h1 css={animationHeader}>
                                    <FormattedMessage
                                        id="SignupProvisioning.inProgressText.confluence.optimizingNewIdeas"
                                        defaultMessage="Optimizing for new ideas..."
                                    />
                                </h1>
                            )}
                            {currentIndex === 3 && (
                                <h1 css={animationHeader}>
                                    <FormattedMessage
                                        id="SignupProvisioning.inProgressText.confluence.limitlessColaboration"
                                        defaultMessage="Loading limitless collaboration..."
                                    />
                                </h1>
                            )}
                            {currentIndex === 4 && (
                                <h1 css={animationHeader}>
                                    <FormattedMessage
                                        id="SignupProvisioning.inProgressText.confluence.letsGo"
                                        defaultMessage="Lets go!"
                                    />
                                </h1>
                            )}
                        </div>
                        <div css={animationSubtext}>
                            <FormattedMessage
                                id="SignupProvisioning.inProgressText.confluence.keepWindowOpen"
                                defaultMessage="Keep this window open while Confluence is loading."
                            />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export const LoadingAnimation = withFeatureExposed(
    "cfind-1867-better-provisioning-user-experience",
    "Test",
    LoadingAnimationComponent
);
